import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Star, Play, Truck, Store, ArrowRight, Calendar, ChevronRight, ExternalLink, Ticket } from 'lucide-react';
import ArtistCard from '@/components/ArtistCard';
import EventCard from '@/components/EventCard';
import AdCard from '@/components/AdCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { usePlayer } from '@/context/PlayerContext';

const API = "/api";

const VENUE_URLS = {
  "Gluek's": "https://www.glueks.com/",
  "Day Block Brewing": "https://dayblockbrewing.com/",
  "Green Room": "https://www.greenroommn.com/events",
  "Bunker's": "https://bunkersmusic.com/calendar",
  "Dakota": "https://www.dakotacooks.com/events/"
};

const VENUE_SLUGS = {
  "Gluek's": "glueks",
  "Day Block Brewing": "dayblock",
  "Green Room": "greenroom",
  "Bunker's": "bunkers",
  "Dakota": "dakota",
};

const Discovery = () => {
  const [artists, setArtists] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [events, setEvents] = useState([]);
  const [ads, setAds] = useState([]);
  const [featured, setFeatured] = useState({ artists: [], food_trucks: [], vendors: [] });
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [layoutConfig, setLayoutConfig] = useState(null);
  const [featuredWeekendEvents, setFeaturedWeekendEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { playTrack } = usePlayer();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Seed data first, then fetch
      await axios.post(`${API}/seed`).catch(() => {});
      
      const [artistsRes, tracksRes, eventsRes, adsRes, featuredRes, layoutRes, trucksRes, vendorsRes, weekendRes] = await Promise.all([
        axios.get(`${API}/artists`),
        axios.get(`${API}/tracks?limit=50`),
        axios.get(`${API}/events?limit=30`),
        axios.get(`${API}/ads`),
        axios.get(`${API}/featured`).catch(() => ({ data: { artists: [], food_trucks: [], vendors: [] } })),
        axios.get(`${API}/homepage/layout`).catch(() => ({ data: { sections: null } })),
        axios.get(`${API}/food-trucks`).catch(() => ({ data: [] })),
        axios.get(`${API}/vendors`).catch(() => ({ data: [] })),
        axios.get(`${API}/events/featured-weekend`).catch(() => ({ data: [] }))
      ]);
      
      setArtists(artistsRes.data);
      setTracks(tracksRes.data);
      setEvents(eventsRes.data);
      setAds(adsRes.data);
      setFeatured(featuredRes.data);
      setLayoutConfig(layoutRes.data.sections);
      setFoodTrucks(trucksRes.data);
      setVendors(vendorsRes.data);
      setFeaturedWeekendEvents(weekendRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if a section is visible based on layout config
  const isSectionVisible = (sectionId) => {
    if (!layoutConfig) return true;
    const section = layoutConfig.find(s => s.id === sectionId);
    return section ? section.visible : true;
  };

  // Get section order
  const getSectionOrder = (sectionId) => {
    if (!layoutConfig) return 999;
    const section = layoutConfig.find(s => s.id === sectionId);
    return section ? section.order : 999;
  };

  if (loading) {
    return (
      <div className="space-y-8" data-testid="discovery-loading">
        <Skeleton className="h-64 w-full" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="aspect-video" />
          ))}
        </div>
      </div>
    );
  }

  const featuredArtist = artists[0];
  const otherArtists = artists.slice(1);
  const premiumAd = ads.find(a => a.is_premium);
  const regularAds = ads.filter(a => !a.is_premium);

  return (
    <div className="space-y-12 max-w-full overflow-hidden" data-testid="discovery-page">
      {/* Logo Header with Background Image */}
      {isSectionVisible('header') && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-xl mb-6"
          style={{ order: getSectionOrder('header') }}
        >
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: 'url(/images/twin-tube-bg.jpg)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />
          
          {/* Content */}
          <div className="relative text-center py-8 px-4">
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight">
              <span className="text-purple-400 drop-shadow-[0_0_20px_rgba(168,85,247,0.6)]">TWIN</span>
              <span className="text-blue-400 ml-2 drop-shadow-[0_0_20px_rgba(59,130,246,0.6)]">TUBE</span>
            </h1>
            <p className="text-sm text-gray-300 mt-2">Twin Cities Creative Network</p>
          </div>
        </motion.div>
      )}

      {/* Artist Registration CTA */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15 }}
        className="relative overflow-hidden rounded-xl border border-purple-500/30 bg-gradient-to-r from-purple-900/40 via-card to-indigo-900/40 p-6"
        data-testid="artist-cta-banner"
      >
        {/* Subtle animated glow */}
        <motion.div
          className="absolute -inset-1 rounded-xl bg-gradient-to-r from-purple-600/20 via-transparent to-indigo-600/20 blur-xl"
          animate={{ opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="relative flex flex-col sm:flex-row items-center gap-4 sm:justify-between">
          <div className="text-center sm:text-left">
            <motion.h2
              className="font-heading text-xl sm:text-2xl font-bold tracking-tight"
              animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
              transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
              style={{
                backgroundImage: "linear-gradient(90deg, #e2e8f0, #c084fc, #e2e8f0, #818cf8, #e2e8f0)",
                backgroundSize: "200% auto",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Your music deserves a bigger local audience!
            </motion.h2>
            <p className="text-sm text-muted-foreground mt-1">
              Twin Cities artists — join the movement and share your sound with the community.
            </p>
          </div>
          <motion.div
            animate={{ scale: [1, 1.04, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <Link
              to="/register/band"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-bold text-sm transition-colors"
              data-testid="artist-register-cta"
            >
              Join Twin Tube <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </motion.div>

      {/* Featured Weekend Events - This Weekend's Spotlight */}
      {featuredWeekendEvents.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
          data-testid="this-weekend-section"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              <h2 className="font-heading text-xl font-bold tracking-tight">This Weekend's Spotlight</h2>
            </div>
            <Link to="/calendar" className="text-xs text-purple-400 tracking-widest uppercase hover:text-purple-300 transition flex items-center gap-1">
              All Events <ChevronRight size={14} />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {featuredWeekendEvents.slice(0, 3).map((event, index) => {
              const isToday = new Date().toLocaleDateString() === new Date(event.date + 'T12:00:00').toLocaleDateString();
              return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="relative overflow-hidden rounded-xl border border-purple-500/30 group"
                  data-testid={`weekend-event-${event.id}`}
                >
                  {event.image_url && (
                    <div className="absolute inset-0 z-0">
                      <img 
                        src={event.image_url} 
                        alt={event.title}
                        className="w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-transparent" />
                    </div>
                  )}
                  
                  <div className="relative z-10 p-5">
                    <div className="absolute top-3 right-3 flex gap-1.5">
                      {isToday && (
                        <span className="px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wide">
                          Tonight
                        </span>
                      )}
                      <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-[10px] font-semibold rounded-full backdrop-blur-sm">
                        Live Music
                      </span>
                    </div>
                    <div className="space-y-2">
                      {VENUE_SLUGS[event.venue] ? (
                        <Link to={`/venue/${VENUE_SLUGS[event.venue]}`} className="text-purple-300 text-sm font-medium hover:text-purple-200 transition">{event.venue}</Link>
                      ) : (
                        <p className="text-purple-300 text-sm font-medium">{event.venue}</p>
                      )}
                      <h3 className="text-lg font-bold text-white">{event.title}</h3>
                      <div className="flex items-center gap-4 text-sm text-gray-300">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </span>
                        <span>{event.time}</span>
                      </div>
                      {event.description && (
                        <p className="text-gray-300 text-sm line-clamp-2">{event.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        {event.ticket_url && (
                          <a 
                            href={event.ticket_url.startsWith('http') ? event.ticket_url : `https://${event.ticket_url}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 text-xs font-semibold transition"
                            onClick={e => e.stopPropagation()}
                          >
                            <Ticket size={12} /> Get Tickets
                          </a>
                        )}
                        <a 
                          href={VENUE_URLS[event.venue] || event.source_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-purple-400 hover:text-purple-300 text-xs"
                        >
                          Venue Info <ExternalLink size={11} />
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.section>
      )}

      {/* Upcoming Events Section - At Top */}
      {isSectionVisible('upcoming_events') && events.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ order: getSectionOrder('upcoming_events') }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-400" />
              <h2 className="font-heading text-xl font-bold tracking-tight">Upcoming Events</h2>
            </div>
            <Link to="/calendar">
              <Button variant="ghost" size="sm" className="text-purple-400 hover:text-purple-300">
                View Calendar
                <ChevronRight size={16} className="ml-1" />
              </Button>
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {(() => {
              const spotlightIds = new Set(featuredWeekendEvents.map(e => e.id));
              const deduped = events.filter(e => !spotlightIds.has(e.id));
              // Show one event per venue for variety
              const seenVenues = new Set();
              const varied = deduped.filter(e => {
                const venue = e.venue || e.source;
                if (seenVenues.has(venue)) return false;
                seenVenues.add(venue);
                return true;
              });
              return varied.slice(0, 4).map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <EventCard event={event} size="compact" />
                </motion.div>
              ));
            })()}
          </div>
        </motion.section>
      )}

      {/* Local Sponsors / Ads Section - RIGHT AFTER EVENTS */}
      {isSectionVisible('sponsors') && ads.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ order: getSectionOrder('sponsors') }}
          data-testid="sponsors-section"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-bold tracking-tight">Local Sponsors</h2>
            <span className="text-xs text-secondary tracking-widest uppercase">Support Local</span>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ads.slice(0, 8).map((ad) => (
              <AdCard key={ad.id} ad={ad} />
            ))}
          </div>
        </motion.section>
      )}

      {/* Hero Section */}
      {isSectionVisible('hero') && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{ order: getSectionOrder('hero') }}
        >
          <p className="text-xs text-muted-foreground tracking-widest uppercase mb-4">Now Playing in the Twin Cities</p>
          
          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
            {/* Featured Artist - Large */}
            {featuredArtist && (
              <div className="col-span-full md:col-span-8 row-span-2">
                <ArtistCard artist={featuredArtist} size="hero" />
              </div>
            )}

            {/* Upcoming Event */}
            {events[0] && (
              <div className="col-span-full md:col-span-4 lg:col-span-4 row-span-2">
                <EventCard event={events[0]} size="large" />
              </div>
            )}

            {/* Premium Ad */}
            {premiumAd && (
              <div className="col-span-full md:col-span-4 lg:col-span-6">
                <AdCard ad={premiumAd} />
              </div>
            )}

            {/* Regular Ad */}
            {regularAds[0] && (
              <div className="col-span-full md:col-span-4 lg:col-span-6">
                <AdCard ad={regularAds[0]} />
              </div>
            )}
          </div>
        </motion.section>
      )}

      {/* Latest Tracks - Now showing Artists with their music */}
      {isSectionVisible('listen_now') && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ order: getSectionOrder('listen_now') }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-bold tracking-tight">Listen Now</h2>
            <span className="text-xs text-secondary tracking-widest uppercase">Local Music</span>
          </div>
          
          {/* Group tracks by artist and show artist cards with play button - sorted by track count */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {artists
            .map(artist => ({
              ...artist,
              trackCount: tracks.filter(t => t.artist_id === artist.id).length
            }))
            .filter(artist => artist.trackCount > 0)
            .sort((a, b) => b.trackCount - a.trackCount)
            .slice(0, 8)
            .map((artist) => {
              const artistTracks = tracks.filter(t => t.artist_id === artist.id);
              return (
                <div key={artist.id} className="group relative rounded-sm overflow-hidden bg-card border border-border card-hover">
                  <Link to={`/artist/${artist.id}`}>
                    <div className="aspect-square relative">
                      <img
                        src={artist.image_url || 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400'}
                        alt={artist.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <p className="font-heading font-bold text-lg truncate text-white">{artist.name}</p>
                        <p className="text-sm text-white/70">{artistTracks.length} track{artistTracks.length !== 1 ? 's' : ''} • {artist.genre}</p>
                      </div>
                    </div>
                  </Link>
                  {/* Play button overlay */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      if (artistTracks.length > 0) {
                        playTrack(artistTracks[0], artist);
                      }
                    }}
                    className="absolute top-3 right-3 w-10 h-10 rounded-full bg-primary/90 hover:bg-primary flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Play size={18} className="text-white ml-0.5" />
                  </button>
                </div>
              );
            })}
        </div>
      </motion.section>
      )}

      {/* More Artists */}
      {isSectionVisible('local_artists') && otherArtists.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{ order: getSectionOrder('local_artists') }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-bold tracking-tight">Local Artists</h2>
            <span className="text-xs text-primary tracking-widest uppercase">Minneapolis • St. Paul</span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {otherArtists.map((artist) => (
              <ArtistCard key={artist.id} artist={artist} />
            ))}
          </div>
        </motion.section>
      )}

      {/* Upcoming Events */}
      {isSectionVisible('upcoming_shows') && events.length > 1 && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          style={{ order: getSectionOrder('upcoming_shows') }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl font-bold tracking-tight">Upcoming Shows</h2>
            <a href="/events" className="text-sm text-primary hover:text-primary/80 transition-colors">View All</a>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.slice(1).map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </motion.section>
      )}

      {/* Featured This Week Section */}
      {isSectionVisible('featured_week') && (featured.artists?.length > 0 || featured.food_trucks?.length > 0 || featured.vendors?.length > 0) && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="p-6 rounded-sm bg-gradient-to-br from-primary/20 via-card to-secondary/10 border border-primary/30"
          data-testid="featured-section"
          style={{ order: getSectionOrder('featured_week') }}
        >
          <div className="flex items-center gap-2 mb-6">
            <Star className="text-secondary fill-secondary" size={20} />
            <h2 className="font-heading text-2xl font-bold tracking-tight">Featured This Week</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Featured Artists */}
            {featured.artists?.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm text-muted-foreground tracking-widest uppercase flex items-center gap-2">
                  <Play size={14} className="text-primary" /> Artist Spotlight
                </h3>
                {featured.artists.slice(0, 2).map((artist) => (
                  <Link to={`/artist/${artist.id}`} key={artist.id}>
                    <div className="flex items-center gap-3 p-3 rounded-sm bg-card/50 hover:bg-card transition-colors">
                      <img src={artist.image_url} alt={artist.name} className="w-12 h-12 rounded-sm object-cover" />
                      <div>
                        <p className="font-medium">{artist.name}</p>
                        <p className="text-xs text-muted-foreground">{artist.genre}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Featured Businesses - Gilded Mirror, etc. */}
            {featured.vendors?.filter(v => v.subscription_tier || v.category?.toLowerCase().includes('barber') || v.category?.toLowerCase().includes('salon')).length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm text-muted-foreground tracking-widest uppercase flex items-center gap-2">
                  <Star size={14} className="text-yellow-400" /> Featured Business
                </h3>
                {featured.vendors.filter(v => v.subscription_tier || v.category?.toLowerCase().includes('barber') || v.category?.toLowerCase().includes('salon')).slice(0, 2).map((biz) => (
                  <a href={biz.website || '#'} target="_blank" rel="noopener noreferrer" key={biz.id}>
                    <div className="flex items-center gap-3 p-3 rounded-sm bg-card/50 hover:bg-card transition-colors">
                      <img src={biz.image_url} alt={biz.name} className="w-12 h-12 rounded-sm object-cover" onError={e => { e.target.src = 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100'; }} />
                      <div>
                        <p className="font-medium">{biz.name}</p>
                        <p className="text-xs text-muted-foreground">{biz.category}</p>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}

            {/* Featured Food Trucks */}
            {featured.food_trucks?.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm text-muted-foreground tracking-widest uppercase flex items-center gap-2">
                  <Truck size={14} className="text-secondary" /> Food Truck of the Week
                </h3>
                {featured.food_trucks.slice(0, 2).map((truck) => (
                  <Link to="/food-trucks" key={truck.id}>
                    <div className="flex items-center gap-3 p-3 rounded-sm bg-card/50 hover:bg-card transition-colors">
                      <img src={truck.image_url} alt={truck.name} className="w-12 h-12 rounded-sm object-cover" />
                      <div>
                        <p className="font-medium">{truck.name}</p>
                        <p className="text-xs text-muted-foreground">{truck.cuisine}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Featured Vendors */}
            {featured.vendors?.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm text-muted-foreground tracking-widest uppercase flex items-center gap-2">
                  <Store size={14} className="text-primary" /> Artisan Spotlight
                </h3>
                {featured.vendors.filter(v => !v.subscription_tier && !v.category?.toLowerCase().includes('barber') && !v.category?.toLowerCase().includes('salon')).slice(0, 2).map((vendor) => (
                  <Link to="/vendors" key={vendor.id}>
                    <div className="flex items-center gap-3 p-3 rounded-sm bg-card/50 hover:bg-card transition-colors">
                      <img src={vendor.image_url} alt={vendor.name} className="w-12 h-12 rounded-sm object-cover" />
                      <div>
                        <p className="font-medium">{vendor.name}</p>
                        <p className="text-xs text-muted-foreground">{vendor.category}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </motion.section>
      )}

      {/* Shorts CTA */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <Link to="/shorts">
          <div className="relative overflow-hidden rounded-sm p-8 bg-gradient-to-r from-primary/30 via-background to-secondary/30 border border-border hover:border-primary/50 transition-colors group">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-2xl font-bold tracking-tight mb-2">Watch Shorts</h2>
                <p className="text-muted-foreground">TikTok-style videos from the Twin Cities music scene</p>
              </div>
              <Button variant="secondary" className="gap-2 group-hover:gap-3 transition-all">
                Watch Now <ArrowRight size={16} />
              </Button>
            </div>
            
            {/* Animated bars */}
            <div className="absolute right-32 top-1/2 -translate-y-1/2 flex items-end gap-1 opacity-30">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="w-2 bg-primary rounded-full visualizer-bar"
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </div>
          </div>
        </Link>
      </motion.section>
    </div>
  );
};

export default Discovery;
